/** PDP-only adaptation of the homepage's masked fine-sand assembly. No replay or scroll trigger. */
const heading = /(?:^|\/)products\/[^/]+\/?$/.test(location.pathname)
  ? document.querySelector('main [data-testid="product-information-details"] h1') : null;
const reduced = matchMedia('(prefers-reduced-motion: reduce)');
const curve = p => `calc(${p} * ${p} * ${p} * (${p} * (${p} * 6 - 15) + 10))`;
const progress = (start, duration) => `clamp(0,calc((var(--tb148-time) - ${start}) / ${duration}),1)`;
const settle = 1500;
const timeScale = settle / 900;
// Preserve the approved 900ms timing profile, stretching every phase proportionally.
const grains = [
  {rise:110, tail:75, fade:214, motion:854, delay:0},
  {rise:136, tail:121, fade:271, motion:854, delay:20},
  {rise:118, tail:95, fade:242, motion:842, delay:40},
  {rise:156, tail:138, fade:294, motion:831, delay:69}
].map(timing => Object.fromEntries(
  Object.entries(timing).map(([name, value]) => [name, value * timeScale])
));

async function assemble() {
  if (!heading || heading.dataset.tb148Played || reduced.matches || document.hidden ||
      !window.CSS?.registerProperty || !window.Intl?.Segmenter ||
      !CSS.supports('mask-image', 'linear-gradient(90deg,rgb(0 0 0 / calc(.5 * .5)),#000)')) return;
  const requested = performance.now();
  await document.fonts?.ready;
  // A late font download must never make an already-read title disappear and reassemble.
  if (performance.now() - requested > 250 || !heading.isConnected || reduced.matches ||
      document.hidden || heading.dataset.tb148Played || getComputedStyle(heading).direction === 'rtl') return;
  heading.dataset.tb148Played = 'true';
  const original = [...heading.childNodes];
  const text = heading.textContent;
  const before = heading.getBoundingClientRect();
  if (!text.trim() || !before.width || !before.height) return;
  const abort = new AbortController();
  const options = {signal:abort.signal};
  const properties = new Map();
  let timer;
  let ink;
  let finished = false;
  const set = (name, value) => {
    if (!properties.has(name)) properties.set(name, [heading.style.getPropertyValue(name), heading.style.getPropertyPriority(name)]);
    heading.style.setProperty(name, String(value));
  };
  const finish = () => {
    if (finished) return;
    finished = true;
    clearTimeout(timer);
    abort.abort();
    heading.classList.remove('tb148-title');
    if (ink && heading.contains(ink)) heading.replaceChildren(...original);
    for (const [name, [value, priority]] of properties) {
      if (value) heading.style.setProperty(name, value, priority);
      else heading.style.removeProperty(name);
    }
  };
  try {
    // Original nodes are restored after the effect; the only accessible title stays in normal flow.
    ink = document.createElement('span');
    ink.className = 'tb148-ink';
    ink.textContent = text;
    const mobile = matchMedia('(max-width:749px)').matches;
    const fields = grains.slice(0, mobile ? 2 : 4).map((_, i) => {
      const field = document.createElement('span');
      field.className = `tb148-field tb148-field-${i + 1}`;
      field.setAttribute('aria-hidden', 'true');
      const dust = document.createElement('span');
      dust.className = `tb148-dust tb148-dust-${i + 1}`;
      dust.textContent = text;
      field.append(dust);
      return field;
    });
    heading.replaceChildren(ink, ...fields);
    heading.classList.add('tb148-title');
    const after = heading.getBoundingClientRect();
    if (['x', 'y', 'width', 'height'].some(k => Math.abs(after[k] - before[k]) > .5)) throw new Error('Title layout changed');
    const box = ink.getBoundingClientRect();
    const rows = [];
    const segments = new Intl.Segmenter(undefined, {granularity:'grapheme'}).segment(text);
    for (const {segment, index} of segments) {
      if (!segment.trim()) continue;
      const range = document.createRange();
      range.setStart(ink.firstChild, index);
      range.setEnd(ink.firstChild, index + segment.length);
      const rect = [...range.getClientRects()].find(r => r.width > 0 && r.height > 0);
      if (!rect) continue;
      let row = rows.find(r => Math.abs(r.top - rect.top) < 1);
      if (!row) { row = {top:rect.top, height:rect.height, glyphs:[]}; rows.push(row); }
      row.glyphs.push({left:Math.max(0, (rect.left - box.left) / box.width * 100)});
    }
    if (!rows.length) throw new Error('Title metrics unavailable');
    rows.sort((a, b) => a.top - b.top);
    // Bound mask complexity for unusually long future titles without changing their wording or layout.
    const count = rows.reduce((n, r) => n + r.glyphs.length, 0);
    const stride = Math.max(1, Math.ceil(count / 96));
    for (const row of rows) row.glyphs = row.glyphs.filter((_, i) => i % stride === 0);
    const regions = rows.reduce((n, r) => n + r.glyphs.length, 0);
    let cursor = 0;
    const inks = [], fieldMasks = fields.map(() => []), sizes = [], positions = [];
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex];
      const boundary = (a, b) => ((a.top + a.height / 2) + (b.top + b.height / 2)) / 2 - box.top;
      const top = rowIndex ? boundary(rows[rowIndex - 1], row) : 0;
      const bottom = rowIndex + 1 < rows.length ? boundary(row, rows[rowIndex + 1]) : box.height;
      sizes.push(`100% ${Math.max(1, bottom - top).toFixed(3)}px`);
      positions.push(`0 ${Math.max(0, top).toFixed(3)}px`);
      const inkStops = [], grainStops = fields.map(() => []);
      for (let i = 0; i < row.glyphs.length; i++, cursor++) {
        const start = regions === 1 ? 0 : cursor / (regions - 1) * 140 * timeScale;
        const inkStart = start + 40 * timeScale;
        const inkName = `--tb148-p${cursor}`;
        set(inkName, progress(inkStart, settle - inkStart));
        const solid = curve(`var(${inkName})`);
        const left = i ? Math.max(row.glyphs[i - 1].left, row.glyphs[i].left) + .25 : 0;
        const right = i + 1 < row.glyphs.length ? Math.max(left, row.glyphs[i + 1].left - .25) : 100;
        const stops = alpha => `rgb(0 0 0 / ${alpha}) ${left.toFixed(4)}%,rgb(0 0 0 / ${alpha}) ${right.toFixed(4)}%`;
        inkStops.push(stops(solid));
        grainStops.forEach((list, group) => {
          const g = grains[group], name = `--tb148-g${group}-${cursor}`;
          set(name, `min(${progress(start, g.rise)},clamp(0,calc((${settle + g.tail * .1} - var(--tb148-time)) / ${g.fade}),1))`);
          // Identical to the corrected Hero: completed glyphs cannot receive late shifted particles.
          list.push(stops(`calc(${curve(`var(${name})`)} * (1 - ${solid}))`));
        });
      }
      inks.push(`linear-gradient(90deg,${inkStops.join(',')})`);
      grainStops.forEach((list, group) => fieldMasks[group].push(`linear-gradient(90deg,${list.join(',')})`));
    }
    const mask = (element, images) => {
      element.style.setProperty('mask-image', images.join(','));
      element.style.setProperty('-webkit-mask-image', images.join(','));
      element.style.setProperty('mask-size', sizes.join(','));
      element.style.setProperty('-webkit-mask-size', sizes.join(','));
      element.style.setProperty('mask-position', positions.join(','));
      element.style.setProperty('-webkit-mask-position', positions.join(','));
    };
    mask(ink, inks);
    fields.forEach((field, i) => {
      mask(field, fieldMasks[i]);
      const p = `var(--tb148-motion-${i})`;
      set(`--tb148-motion-${i}`, progress(grains[i].delay, grains[i].motion));
      set(`--tb148-drift-${i}`, `calc(1 - ${curve(p)})`);
      const bend = `min(clamp(0,calc(${p} / .35),1),clamp(0,calc((1 - ${p}) / .65),1))`;
      set(`--tb148-bend-${i}`, curve(bend));
    });
    heading.addEventListener('animationend', e => { if (e.target === heading && e.animationName === 'tb148-assembly') finish(); }, options);
    heading.addEventListener('animationcancel', e => { if (e.target === heading) finish(); }, options);
    window.addEventListener('resize', finish, options);
    window.addEventListener('pagehide', finish, options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) finish(); }, options);
    reduced.addEventListener('change', e => { if (e.matches) finish(); }, options);
    timer = setTimeout(finish, settle + 150 * timeScale);
  } catch {
    finish();
  }
}

assemble().catch(() => { /* The unmodified semantic title is the default fallback. */ });
